import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SignupContentWrapper from '../components/SignupContentWrapper';
import { minimumValidation } from '../components/SimpleTextFieldComponent';
import { useSelector } from 'react-redux';
import { AppState } from '../../../store';
import SimpleAutocompleteTextFieldComponent from '../components/SimpleAutocompleteTextFieldComponent';
import { PATH } from '../../../routes';
import { pushEvent } from '../../../utils/clevertapUtils';
import {
    CLICK_USER_TITLE_PAGE_OK_BTN,
    VIEW_USER_TITLE_PAGE,
} from '../../../utils/clevertapEvents';
import { useRouteConfig } from '../utils/useRouteConfig';
import { matchSorter } from 'match-sorter';

const STEP_INFO = 2;

const UserTitle: React.FC = () => {
    const { t } = useTranslation();
    const { next, previous } = useRouteConfig(PATH.SIGNUP.STEPS.FOUR);
    const { organizationName, titleList, reduxUserTitle } = useSelector(
        (state: AppState) => ({
            organizationName:
                state.Signup.organizationName || 'your organization',
            titleList: state.Signup.userTitles,
            reduxUserTitle: state.Signup.userTitle,
        }),
    );
    const copy = useMemo(
        () => ({
            heading: t('signup.userTitle.heading', {
                organizationName,
            }),
            required: t('signup.userTitle.errorMessage', {
                organizationName,
            }),
            placeholderText: t('signup.userTitle.placeholderText'),
        }),
        [t, organizationName],
    );

    const titlesCopy = useMemo<
        {
            [key in string]: string | undefined;
        }
    >(
        () => ({
            Senior: t('titleList.Senior'),
            Pastor: t('titleList.Pastor'),
            Secretary: t('titleList.Secretary'),
            Treasurer: t('titleList.Treasurer'),
            Minister: t('titleList.Minister'),
            Bishop: t('titleList.Bishop'),
            Reverend: t('titleList.Reverend'),
            'Arch Bishop': t('titleList.ArchBishop'),
            Manager: t('titleList.Manager'),
            Venerable: t('titleList.Venerable'),
            'Office Staff': t('titleList.OfficeStaff'),
            President: t('titleList.President'),
            Chairman: t('titleList.Chairman'),
            CEO: t('titleList.CEO'),
            'Board Member': t('titleList.BoardMember'),
        }),
        [t],
    );

    useEffect(() => {
        pushEvent(VIEW_USER_TITLE_PAGE);
    }, []);

    const [autocompleteUserTitle, setAutocompleteUserTitle] =
        useState(reduxUserTitle);
    const [isDirty, setIsDirty] = useState(false);

    const onChange = (value: string) => {
        setIsDirty(true);
        setAutocompleteUserTitle(value);
    };

    const value = isDirty ? autocompleteUserTitle : reduxUserTitle;
    const translatedList = useMemo(
        () => titleList.map((x) => titlesCopy[x] || x),
        [titleList, titlesCopy],
    );
    const optionsFullList = useMemo(() => {
        const matched = matchSorter(translatedList, value);

        if (value?.length) {
            const lowerCaseItems = matched.map((x) => x?.toLocaleLowerCase());
            const contains =
                lowerCaseItems.indexOf(value?.toLocaleLowerCase()) > -1;
            return contains ? matched : [value, ...matched];
        } else {
            return matched;
        }
    }, [translatedList, value]);

    return (
        <SignupContentWrapper
            heading={copy.heading}
            stepInfo={STEP_INFO}
            showCloseButton={true}
            backUrl={previous}
        >
            <SimpleAutocompleteTextFieldComponent
                type="text"
                reduxKey="userTitle"
                nextLink={next || ''}
                errorText={copy.required}
                placeholderText={copy.placeholderText}
                validation={minimumValidation}
                options={optionsFullList}
                maxLength={25}
                onChange={onChange}
                okEvent={CLICK_USER_TITLE_PAGE_OK_BTN}
            />
        </SignupContentWrapper>
    );
};

export default UserTitle;
